import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { X, LayoutGrid, Coins, Play, ArrowRight, Crosshair, Crown } from 'lucide-react';
import { VARIANT_SPECS, type GameVariant } from '@draughts/shared';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { useModaleAccessibile } from '../../hooks/useModaleAccessibile';
import { deriveRuleStatements, type RuleCategory } from '../../rules/deriveRules';
import './VademecumVariante.css';

/*
 * Gli stessi tre temi delle pagine regole, nello stesso ordine: chi legge qui le
 * regole di fretta e poi apre la pagina intera deve ritrovare la stessa forma.
 * La categoria `glance` non e' nell'elenco perche' quei fatti sono frasi, e qui
 * i tre dati stanno come numeri (vedi `RulesPage`, che fa la stessa scelta).
 */
const TEMI: { category: RuleCategory; titleKey: string; Icon: typeof ArrowRight }[] = [
  { category: 'movement', titleKey: 'rules.theme.movement', Icon: ArrowRight },
  { category: 'capture', titleKey: 'rules.theme.capture', Icon: Crosshair },
  { category: 'promotion', titleKey: 'rules.theme.promotion', Icon: Crown },
];

interface VademecumVarianteProps {
  /** Variante ATTIVA: chi monta questo pannello l'ha gia' verificato. */
  variant: GameVariant;
  /** Nome della variante, gia' tradotto: lo stesso che si legge nella pill. */
  variantLabel: string;
  /**
   * Partita BENDATA. Cambia una cosa sola, e non e' estetica: salta
   * `aria-modal`. Quell'attributo dice allo screen reader di restringersi al
   * dialogo, e l'annuncio della mossa avversaria e' una regione viva
   * (`role="status"` in BlindMoveAnnouncement) che sta FUORI di qui. Bendati,
   * quell'annuncio E' la damiera: zittirlo per far leggere le regole sarebbe
   * uno scambio che non vogliamo. La trappola del fuoco resta, ed e' quella che
   * impedisce di tabulare dietro al velo.
   */
  bendata?: boolean;
  onClose: () => void;
}

/**
 * Le regole principali della variante in corso, senza uscire dalla partita.
 *
 * Non c'e' nessun testo scritto a mano qui dentro: le frasi le deriva
 * `deriveRuleStatements` dai flag della Master Matrix, ed e' la stessa funzione
 * che riempie le pagine regole pubbliche. Due superfici, una sorgente: non
 * possono divergere.
 *
 * PORTALE, e non per gusto: `.game-header` dichiara `backdrop-filter`, che la
 * rende blocco contenitore per i discendenti `position: fixed` — un pannello
 * montato dentro la riga sarebbe posizionato e tagliato da uno scorrevole alto
 * 46px con `overflow-y: hidden`, cioe' invisibile. E `.game-fullscreen`
 * dichiara `touch-action: none`, quindi un corpo che scorre montato li' dentro
 * non risponderebbe al dito. `FullscreenPanel` fa il portale per le stesse due
 * ragioni.
 *
 * NON c'e' la damiera della posizione iniziale che le pagine regole hanno: in
 * partita bendata questo e' l'unico schermo dove ogni aiuto visivo e' soppresso
 * di proposito, e sarebbe una falla. E non c'e' nessun collegamento alla pagina
 * intera: l'orologio non si ferma, e `target="_blank"` in PWA installata butta
 * fuori dall'app (vedi ChallengeOptionsFields). Il pannello si regge da solo.
 */
export default function VademecumVariante({
  variant,
  variantLabel,
  bendata = false,
  onClose,
}: VademecumVarianteProps) {
  const { t } = useTranslation();

  // Il gesto «indietro» chiude il pannello invece di uscire dalla partita.
  useOverlayBackButton(true, onClose);
  /*
   * Il fuoco entra, Tab non esce, alla chiusura torna a chi l'aveva — e
   * l'Escape lo gestisce il gancio, per questo gli passiamo `onClose`: un
   * secondo ascoltatore scritto a mano qui sarebbe la stessa cosa detta due
   * volte. Gli argomenti sono `true` perche' questo componente si monta solo
   * quando e' aperto (come `FullscreenPanel`), quindi non c'e' nessun
   * `return null` sotto cui un hook potrebbe finire.
   */
  const riferimentoDialogo = useModaleAccessibile<HTMLDivElement>(true, onClose);

  const spec = VARIANT_SPECS[variant];
  const fatti = deriveRuleStatements(spec);

  return createPortal(
    <div className="vdm-overlay" onClick={onClose}>
      {/* `role`/`aria-modal` stanno sul PANNELLO e non sul velo: un dialogo il
          cui nodo e' il velo dichiara come proprio contenuto anche il fondo
          cliccabile. Stessa scelta di `InGameSettingsModal`. */}
      <div
        ref={riferimentoDialogo}
        className="vdm-pannello"
        role="dialog"
        aria-modal={bendata ? undefined : true}
        aria-labelledby="vdm-titolo"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        data-testid="vademecum-variante"
      >
        <div className="vdm-testata">
          <h2 className="vdm-titolo" id="vdm-titolo">
            <span className="vdm-bandiera" aria-hidden="true">{spec.ui.flagEmoji}</span>
            {variantLabel}
          </h2>
          <button
            type="button"
            className="vdm-chiudi"
            onClick={onClose}
            aria-label={t('common.close')}
            data-testid="vademecum-chiudi"
          >
            <X size={20} />
          </button>
        </div>

        <div className="vdm-corpo">
          {/* A colpo d'occhio: i tre dati che si cercano per primi. */}
          <div className="vdm-occhio">
            <div className="vdm-dato">
              <span className="vdm-dato-valore">{spec.board.size}×{spec.board.size}</span>
              <span className="vdm-dato-etichetta">
                <LayoutGrid size={11} aria-hidden="true" /> {t('rules.glance.board')}
              </span>
            </div>
            <div className="vdm-dato">
              <span className="vdm-dato-valore">{spec.board.piecesPerPlayer}</span>
              <span className="vdm-dato-etichetta">
                <Coins size={11} aria-hidden="true" /> {t('rules.glance.pieces')}
              </span>
            </div>
            <div className="vdm-dato">
              <span className="vdm-dato-valore">
                {spec.board.blackStarts ? t('game.clock.black') : t('game.clock.white')}
              </span>
              <span className="vdm-dato-etichetta">
                <Play size={11} aria-hidden="true" /> {t('rules.glance.starts')}
              </span>
            </div>
          </div>

          {/* I tre temi. Nessuno dei tre puo' essere vuoto: `deriveRules` ha una
              frase incondizionata per ciascuno. Il filtro resta comunque, che e'
              quel che fa `RulesPage`, perche' la garanzia sta nell'altro file. */}
          {TEMI.map(({ category, titleKey, Icon }) => {
            const voci = fatti.filter((f) => f.category === category);
            if (voci.length === 0) return null;
            return (
              <section key={category} className="vdm-tema">
                <div className="vdm-tema-testata">
                  <Icon size={15} aria-hidden="true" />
                  <span className="vdm-tema-nome">{t(titleKey)}</span>
                </div>
                <ul>
                  {voci.map((f) => (
                    <li key={f.key}>{t(f.key, f.values)}</li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
